/**
 * Markdown rendering for the preview pane.
 *
 * Two jobs beyond plain rendering:
 * 1. Resolve portable `jnote-att://` references to proxied object URLs so
 *    attachments appear inline.
 * 2. Sanitise the output — note bodies are user content fetched from a bucket,
 *    so raw HTML and `javascript:` URLs must never reach the DOM.
 */
import { isValidElement, useMemo, useState, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { attachmentURL, toLocal } from '../lib/sync/attachmentCodec.js';
import { attachmentKey, vaultAttachmentKey } from '../lib/sync/paths.js';
import { objectURL } from '../lib/api/storage.js';
import { useOverlayScrollbar } from '../lib/ui/overlayScroll.js';

/**
 * Rewrites `jnote-att://id/file` into a fetchable same-origin URL.
 *
 * Filenames may appear raw (macOS writes spaces verbatim, e.g.
 * `Screenshot 2026-09-11 at 09.09.25.png`) or percent-encoded (Web
 * insertion). Both forms must resolve to the same object URL — otherwise the
 * markdown destination keeps raw spaces, CommonMark refuses to parse the
 * image, and the preview shows literal `![…](…)` text instead of the image.
 */
export function resolveAttachmentURLs(
  markdown: string,
  options: {
    vaultedNoteIds?: Set<string>;
    /** Decrypted vault attachments, keyed by their portable reference. */
    attachmentURLs?: Map<string, string>;
  } = {},
): string {
  const vaulted = options.vaultedNoteIds ?? new Set<string>();
  const overrides = options.attachmentURLs ?? new Map<string, string>();
  return toLocal(markdown, (id, filename) => {
    // A decrypted vault attachment renders from its in-memory blob, since the
    // stored object is opaque ciphertext. Overrides are keyed canonically
    // (encoded), so look up through the same form regardless of how the body
    // spells the reference.
    const decryptedURL = overrides.get(attachmentURL(id, filename));
    if (decryptedURL) return decryptedURL;
    const key = vaulted.has(id) ? vaultAttachmentKey(id, filename) : attachmentKey(id, filename);
    return objectURL(key);
  });
}

const SAFE_PROTOCOL = /^(https?:|mailto:|tel:|\/|#|data:image\/)/i;

/** Blocks javascript:/vbscript: and other dangerous URL schemes. */
function isSafeURL(url: string | undefined): boolean {
  if (!url) return false;
  return SAFE_PROTOCOL.test(url.trim());
}

interface MarkdownViewProps {
  source: string;
  vaultedNoteIds?: Set<string>;
  attachmentURLs?: Map<string, string>;
}

/** Flattens a react-markdown code child back into raw text. */
function nodeText(node: ReactNode): string {
  if (node == null || node === false) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return nodeText(node.props.children);
  return '';
}

/**
 * Fenced ```svg block rendered as an image, mirroring the Mac
 * `SVGCodeBlockView`: the SVG is decoded by the browser's image loader
 * (scripts inside the SVG never run), max display width 600, with a
 * code-block-styled panel and an "Unable to render SVG" fallback.
 */
function SVGCodeBlock({ code }: { code: string }) {
  const [failed, setFailed] = useState(false);
  // Parity with the Mac pipeline: `currentColor` inside an <img> computes
  // to black and disappears on the dark panel — normalize to white.
  const svg = useMemo(() => code.replace(/currentColor/g, 'white'), [code]);
  const src = useMemo(
    () => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`,
    [svg],
  );
  if (failed) {
    return (
      <div className="svg-block svg-block-failed">Unable to render SVG</div>
    );
  }
  return (
    <div className="svg-block">
      <img src={src} alt="" draggable={false} onError={() => setFailed(true)} />
    </div>
  );
}

/**
 * Fenced code block with a floating horizontal capsule scrollbar (macOS
 * overlay parity) instead of the classic always-on bar Chrome draws on
 * Windows.
 */
function CodeBlock({ children }: { children: ReactNode }) {
  const scroll = useOverlayScrollbar<HTMLPreElement>({ axis: 'x' });
  return (
    <div className="ob-host ob-host-code" ref={scroll.hostRef}>
      <pre ref={scroll.scrollerRef}>{children}</pre>
    </div>
  );
}

export function MarkdownView({ source, vaultedNoteIds, attachmentURLs }: MarkdownViewProps) {
  const resolved = useMemo(
    () => resolveAttachmentURLs(source, { vaultedNoteIds, attachmentURLs }),
    [source, vaultedNoteIds, attachmentURLs],
  );

  return (
    <div className="markdown-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={(url) => (isSafeURL(url) ? url : '')}
        components={{
          a: ({ href, children }) => (
            <a
              href={href}
              target={href?.startsWith('/') ? undefined : '_blank'}
              rel={href?.startsWith('/') ? undefined : 'noreferrer noopener'}
            >
              {children}
            </a>
          ),
          pre: ({ children }) => {
            const child = Array.isArray(children) ? children[0] : children;
            if (
              isValidElement<{ className?: string; children?: ReactNode }>(child) &&
              /(?:^|\s)language-svg(?:\s|$)/.test(child.props.className ?? '')
            ) {
              const code = nodeText(child.props.children);
              return <SVGCodeBlock key={code} code={code} />;
            }
            return <CodeBlock>{children}</CodeBlock>;
          },
          img: ({ src, alt }) =>
            src ? (
              <img src={src} alt={alt ?? ''} style={{ maxWidth: '100%', borderRadius: 6 }} />
            ) : null,
        }}
      >
        {resolved}
      </ReactMarkdown>
    </div>
  );
}
